<template>
  <section class="security-page">
    <div class="container mt-5" style="max-width: 640px;">
      <div class="card shadow border-0 rounded-3">
        <div class="card-body p-4">
          <h2 class="h5 mb-3">Double authentification (2FA)</h2>

          <div v-if="loadingStatus" class="text-muted">Chargement...</div>

          <!-- Deja active -->
          <div v-else-if="enabled && !setup">
            <div class="alert alert-success py-2">
              La double authentification est <strong>active</strong> sur votre compte.
            </div>
            <p class="small text-muted">
              A chaque connexion depuis un nouveau navigateur, un code a 6 chiffres de votre
              application d'authentification vous sera demande.
            </p>
            <button class="btn btn-outline-danger btn-sm" @click="disable" :disabled="busy">Desactiver la 2FA</button>
          </div>

          <!-- Pas encore active : bouton demarrer -->
          <div v-else-if="!enabled && !setup">
            <p class="text-muted">
              Protegez votre acces admin avec un code temporaire genere par une application
              (Microsoft Authenticator, Google Authenticator). Vous pourrez alors vous connecter
              depuis n'importe quel reseau, sans blocage par adresse IP.
            </p>
            <button class="btn btn-primary" @click="startSetup" :disabled="busy">
              {{ busy ? '...' : 'Activer la double authentification' }}
            </button>
          </div>

          <!-- Enrolement en cours -->
          <div v-else-if="setup">
            <ol class="small mb-3">
              <li>Ouvrez votre application d'authentification.</li>
              <li>Scannez le QR code ci-dessous, ou saisissez la cle manuelle.</li>
              <li>Entrez le code a 6 chiffres affiche pour confirmer.</li>
            </ol>

            <div class="text-center mb-3">
              <img v-if="setup.qr_png" :src="setup.qr_png" alt="QR code 2FA" style="width: 200px; height: 200px;">
              <div v-else class="alert alert-info py-2 small">Saisissez la cle manuelle ci-dessous dans l'application.</div>
            </div>

            <div class="mb-3">
              <label class="form-label small text-muted">Cle manuelle (si le scan est impossible)</label>
              <input class="form-control text-center" style="letter-spacing:2px;" :value="setup.manual_key" readonly @focus="$event.target.select()">
            </div>

            <form @submit.prevent="confirm">
              <div class="mb-3">
                <label class="form-label">Code de verification</label>
                <input type="text" inputmode="numeric" maxlength="6" class="form-control text-center"
                       style="letter-spacing:4px; font-size:1.3rem;" placeholder="000000" required v-model="code">
              </div>
              <button type="submit" class="btn btn-primary" :disabled="busy">{{ busy ? 'Verification...' : 'Confirmer et activer' }}</button>
              <button type="button" class="btn btn-link text-muted" @click="cancelSetup">Annuler</button>
            </form>
          </div>

          <!-- Codes de secours (montres une seule fois) -->
          <div v-if="recoveryCodes.length" class="alert alert-warning mt-4">
            <strong>Codes de secours</strong> — notez-les et gardez-les en lieu sur. Chacun ne
            fonctionne qu'une fois, en cas de perte du telephone. Ils ne seront plus affiches.
            <div class="row mt-2">
              <div class="col-6 font-monospace" v-for="c in recoveryCodes" :key="c">{{ c }}</div>
            </div>
          </div>

          <div v-if="error" class="alert alert-danger py-2 small mt-3 mb-0">{{ error }}</div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import axios from 'axios'

export default {
  name: 'Security',
  data() {
    return {
      loadingStatus: true,
      enabled: false,
      setup: null,
      code: '',
      recoveryCodes: [],
      busy: false,
      error: ''
    }
  },
  async mounted() {
    document.title = 'Securite | Eurekali Admin'
    await this.refresh()
  },
  methods: {
    async refresh() {
      this.loadingStatus = true
      try {
        const { data } = await axios.get('/api/v1/admin/2fa/status/')
        this.enabled = !!data.enabled
      } catch (e) {
        this.error = "Impossible de lire l'etat de la 2FA."
      } finally {
        this.loadingStatus = false
      }
    },
    async startSetup() {
      this.error = ''; this.busy = true; this.recoveryCodes = []
      try {
        const { data } = await axios.post('/api/v1/admin/2fa/setup/')
        this.setup = data
        this.code = ''
      } catch (e) {
        this.error = "Erreur lors de la preparation. Reessayez."
      } finally {
        this.busy = false
      }
    },
    async confirm() {
      this.error = ''; this.busy = true
      try {
        const { data } = await axios.post('/api/v1/admin/2fa/enable/', { code: this.code })
        this.recoveryCodes = data.recovery_codes || []
        this.enabled = true
        this.setup = null
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.error
        this.error = msg || 'Code incorrect.'
      } finally {
        this.busy = false
      }
    },
    cancelSetup() {
      this.setup = null; this.code = ''; this.error = ''
    },
    async disable() {
      if (!confirm('Desactiver la double authentification sur votre compte ?')) return
      this.error = ''; this.busy = true
      try {
        await axios.post('/api/v1/admin/2fa/disable/', {})
        this.enabled = false
        this.recoveryCodes = []
      } catch (e) {
        this.error = 'Erreur lors de la desactivation.'
      } finally {
        this.busy = false
      }
    }
  }
}
</script>
